import { useState, useRef, useEffect } from "react";
import { ArrowUp, Phone } from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { DrEmeryOpener } from "@/shared/components/dashboard/DrEmeryOpener";
import { SupervisorOrb } from "@/shared/components/supervisor/SupervisorOrb";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

const PLACEHOLDER: Record<string, string> = {
  en: "Ask anything. Press Enter to send.",
  uk: "Запитайте будь-що. Натисніть Enter, щоб надіслати.",
  pl: "Zapytaj o cokolwiek. Naciśnij Enter, aby wysłać.",
  ro: "Întreabă orice. Apasă Enter pentru a trimite.",
};

const VOICE_LABEL: Record<string, string> = {
  en: "Voice",
  uk: "Голос",
  pl: "Głos",
  ro: "Voce",
};

const FALLBACK_GREETING: Record<string, Record<string, string>> = {
  en: {
    consulting: "Let's think this through together.",
    transplant: "I'm here when you're ready.",
    "mental-health": "What's on your mind?",
    default: "Let's think this through together.",
  },
  uk: {
    default: "Давайте подумаємо разом.",
  },
  pl: { default: "Pomyślmy o tym razem." },
  ro: { default: "Să ne gândim împreună." },
};

function fallbackGreeting(locale: string, vertical: string): string {
  const byLocale = FALLBACK_GREETING[locale] ?? FALLBACK_GREETING.en;
  return byLocale[vertical] ?? byLocale.default ?? FALLBACK_GREETING.en.default;
}

function ChatComposerWidgetImpl(_: WidgetProps) {
  const { locale } = useLanguage();
  const { vertical, verticalConfig } = useVertical();
  const supervisorName = verticalConfig.supervisor?.identity.name;

  const [value, setValue] = useState("");
  const [isStartingVoice, setIsStartingVoice] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow textarea, capped.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    sessionStorage.setItem("supervisorInitialMessage", trimmed);
    (window as any).navigate?.("/supervisor");
  };

  const startVoiceCall = () => {
    if (isStartingVoice) return;
    setIsStartingVoice(true);
    sessionStorage.setItem("supervisorStartVoice", "true");
    // Brief delay so the button press animation can register before nav.
    setTimeout(() => (window as any).navigate?.("/supervisor"), 180);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const sendDisabled = value.trim().length === 0;

  return (
    <section
      className="flex flex-col justify-center gap-10 py-12 min-h-[380px]"
      style={{ minHeight: "clamp(380px, 48vh, 560px)" }}
    >
      {/* Orb + typed greeting */}
      <div className="flex flex-col items-center gap-5">
        <SupervisorOrb size={56} color="var(--accent)" />
        <DrEmeryOpener
          locale={locale as "en" | "uk" | "pl" | "ro"}
          supervisorName={supervisorName}
          vertical={vertical}
          variant="inline"
          fallback={fallbackGreeting(locale, vertical)}
        />
      </div>

      {/* Composer */}
      <div className="mx-auto w-full max-w-[760px]">
        <div className="flex items-end gap-2.5 border border-[var(--border)] rounded-[var(--radius)] bg-[var(--bg)] px-4 py-3.5 focus-within:border-[var(--accent)] transition-colors">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={PLACEHOLDER[locale] ?? PLACEHOLDER.en}
            rows={1}
            className="flex-1 resize-none bg-transparent text-[16px] text-[var(--fg)] placeholder:text-[var(--faint)] focus:outline-none py-2 leading-[1.55] min-h-[36px]"
          />

          {/* Voice */}
          <button
            type="button"
            onClick={startVoiceCall}
            disabled={isStartingVoice}
            aria-label={VOICE_LABEL[locale] ?? VOICE_LABEL.en}
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-[var(--radius)] border border-[var(--border)] text-[var(--muted)] hover:text-[var(--fg)] hover:border-[var(--muted)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <Phone size={16} strokeWidth={1.75} />
          </button>

          {/* Send */}
          <button
            type="button"
            onClick={submit}
            disabled={sendDisabled}
            aria-label="Send"
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-[var(--radius)] bg-[var(--accent)] text-[var(--bg)] disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-90 transition-opacity cursor-pointer"
          >
            <ArrowUp size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </section>
  );
}

registerWidget({
  id: "chatComposer",
  component: ChatComposerWidgetImpl,
  supportedSlots: new Set(["mainColumn"]),
  displayName: "Chat Composer",
});

export const ChatComposerWidget = ChatComposerWidgetImpl;
